/*
  Búsqueda predictiva de Safe Care Medical.

  Elemento personalizado <predictive-search> que sigue el patrón oficial de Shopify
  y el patrón combobox con listbox de las ARIA Authoring Practices del W3C.
  https://shopify.dev/docs/storefronts/themes/navigation-search/search/predictive-search

  Sin dependencias externas. Si algo falla, el formulario sigue enviando a /search
  como una búsqueda normal.
*/
(function () {
  'use strict';

  var ESPERA_MS = 300;
  var MINIMO_CARACTERES = 2;
  var CACHE_MAXIMO = 30;
  var RETARDO_CIERRE_MS = 160;

  function PredictiveSearchDefinicion() {
    class PredictiveSearch extends HTMLElement {
      constructor() {
        super();

        this.cache = new Map();
        this.controlador = null;
        this.temporizador = null;
        this.temporizadorCierre = null;
        this.indiceActivo = -1;
        this.opciones = [];
        this.terminoActual = '';
        this.abierto = false;

        this.alEscribir = this.alEscribir.bind(this);
        this.alTeclear = this.alTeclear.bind(this);
        this.alEnviar = this.alEnviar.bind(this);
        this.alClicFuera = this.alClicFuera.bind(this);
        this.alSalirFoco = this.alSalirFoco.bind(this);
        this.alEntrarFoco = this.alEntrarFoco.bind(this);
      }

      connectedCallback() {
        this.formulario = this.querySelector('form');
        this.entrada = this.querySelector('input[type="search"]');
        this.panel = this.querySelector('[data-sc-ps-resultados]');
        this.salida = this.querySelector('[data-sc-ps-salida]');
        this.cargando = this.querySelector('[data-sc-ps-cargando]');
        this.estado = this.querySelector('[data-sc-ps-estado]');

        if (!this.formulario || !this.entrada || !this.panel || !this.salida) { return; }

        this.urlBase = this.dataset.url || '/search/suggest';
        this.seccion = this.dataset.seccion || 'predictive-search';
        this.limite = this.dataset.limite || '5';
        this.recursos = this.dataset.recursos || 'product,collection,page,query';
        this.prefijo = (this.id || 'sc-ps') + '__';

        this.entrada.addEventListener('input', this.alEscribir);
        this.entrada.addEventListener('keydown', this.alTeclear);
        this.entrada.addEventListener('focus', this.alEntrarFoco);
        this.formulario.addEventListener('submit', this.alEnviar);
        this.addEventListener('focusout', this.alSalirFoco);
        document.addEventListener('click', this.alClicFuera);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.alClicFuera);
        window.clearTimeout(this.temporizador);
        window.clearTimeout(this.temporizadorCierre);
        this.abortar();
      }

      /* Consulta */

      alEscribir() {
        var termino = this.entrada.value.trim();
        window.clearTimeout(this.temporizador);

        if (termino.length < MINIMO_CARACTERES) {
          this.abortar();
          this.cerrar();
          this.limpiar();
          return;
        }

        var self = this;
        this.temporizador = window.setTimeout(function () {
          self.consultar(termino);
        }, ESPERA_MS);
      }

      alEntrarFoco() {
        var termino = this.entrada.value.trim();
        if (termino.length >= MINIMO_CARACTERES && termino === this.terminoActual && this.salida.innerHTML.trim() !== '') {
          this.abrir();
        }
      }

      consultar(termino) {
        if (this.cache.has(termino)) {
          this.pintar(this.cache.get(termino), termino);
          return;
        }

        this.abortar();
        this.mostrarCarga(true);

        var url = this.urlBase +
          '?q=' + encodeURIComponent(termino) +
          '&section_id=' + encodeURIComponent(this.seccion) +
          '&resources[type]=' + encodeURIComponent(this.recursos) +
          '&resources[limit]=' + encodeURIComponent(this.limite) +
          '&resources[limit_scope]=each';

        var opciones = {};
        if ('AbortController' in window) {
          this.controlador = new AbortController();
          opciones.signal = this.controlador.signal;
        }

        var self = this;
        fetch(url, opciones)
          .then(function (respuesta) {
            if (!respuesta.ok) { throw new Error('Respuesta ' + respuesta.status); }
            return respuesta.text();
          })
          .then(function (texto) {
            var html = self.extraer(texto);
            self.guardarEnCache(termino, html);
            self.mostrarCarga(false);
            self.pintar(html, termino);
          })
          .catch(function (error) {
            if (error && error.name === 'AbortError') { return; }
            /* Silencio deliberado: el formulario sigue funcionando sin el panel. */
            self.mostrarCarga(false);
            self.cerrar();
            self.limpiar();
          });
      }

      abortar() {
        if (this.controlador) {
          try { this.controlador.abort(); } catch (e) { /* nada */ }
          this.controlador = null;
        }
      }

      extraer(texto) {
        try {
          var doc = new DOMParser().parseFromString(texto, 'text/html');
          var envoltorio = doc.querySelector('#shopify-section-' + this.seccion) || doc.querySelector('.shopify-section');
          if (envoltorio) { return envoltorio.innerHTML; }
          var propio = doc.querySelector('.sc-ps');
          if (propio) { return propio.outerHTML; }
        } catch (e) { /* cae al texto crudo */ }
        return texto;
      }

      guardarEnCache(termino, html) {
        if (this.cache.size >= CACHE_MAXIMO) {
          var primera = this.cache.keys().next();
          if (!primera.done) { this.cache.delete(primera.value); }
        }
        this.cache.set(termino, html);
      }

      /* Pintado */

      pintar(html, termino) {
        this.terminoActual = termino;
        this.salida.innerHTML = html;
        this.unificarIds();
        this.opciones = Array.prototype.slice.call(this.panel.querySelectorAll('[role="option"]'));
        this.indiceActivo = -1;
        this.entrada.removeAttribute('aria-activedescendant');
        this.abrir();
        this.anunciar(this.opciones.length, termino);
      }

      /* Las dos instancias del encabezado inyectan la misma sección, así que
         se antepone un prefijo propio a cada id para que nunca se repitan. */
      unificarIds() {
        var prefijo = this.prefijo;
        var conId = this.salida.querySelectorAll('[id]');
        Array.prototype.forEach.call(conId, function (elemento) {
          if (elemento.id.indexOf(prefijo) !== 0) { elemento.id = prefijo + elemento.id; }
        });
      }

      limpiar() {
        this.salida.innerHTML = '';
        this.opciones = [];
        this.indiceActivo = -1;
        this.terminoActual = '';
        this.entrada.removeAttribute('aria-activedescendant');
      }

      mostrarCarga(activo) {
        if (this.cargando) { this.cargando.hidden = !activo; }
        this.toggleAttribute('data-cargando', !!activo);
        if (activo) {
          this.salida.innerHTML = '';
          this.opciones = [];
          this.indiceActivo = -1;
          this.entrada.removeAttribute('aria-activedescendant');
          this.abrir();
        }
      }

      anunciar(cantidad, termino) {
        if (!this.estado) { return; }
        var mensaje;
        if (cantidad > 0) {
          mensaje = cantidad === 1
            ? '1 resultado para ' + termino
            : cantidad + ' resultados para ' + termino;
        } else {
          mensaje = 'Sin resultados para ' + termino;
        }
        this.estado.textContent = mensaje;
      }

      /* Apertura y cierre */

      abrir() {
        if (this.abierto) { return; }
        this.abierto = true;
        this.panel.hidden = false;
        this.entrada.setAttribute('aria-expanded', 'true');
      }

      cerrar() {
        if (!this.abierto) { return; }
        this.abierto = false;
        this.panel.hidden = true;
        this.entrada.setAttribute('aria-expanded', 'false');
        this.desmarcar();
      }

      /* Teclado */

      alTeclear(evento) {
        var tecla = evento.key;

        if (tecla === 'Escape') {
          if (this.abierto) {
            evento.preventDefault();
            /* No dejamos que el Escape llegue al cajón móvil mientras el panel está abierto. */
            evento.stopPropagation();
            this.cerrar();
          }
          return;
        }

        if (tecla === 'ArrowDown' || tecla === 'ArrowUp') {
          if (!this.abierto && this.salida.innerHTML.trim() !== '') { this.abrir(); }
          if (!this.opciones.length) { return; }
          evento.preventDefault();
          var paso = tecla === 'ArrowDown' ? 1 : -1;
          var siguiente = this.indiceActivo + paso;
          if (siguiente < 0) { siguiente = this.opciones.length - 1; }
          if (siguiente > this.opciones.length - 1) { siguiente = 0; }
          this.marcar(siguiente);
          return;
        }

        if (tecla === 'Home' && this.abierto && this.opciones.length) {
          if (this.indiceActivo > -1) { evento.preventDefault(); this.marcar(0); }
          return;
        }

        if (tecla === 'End' && this.abierto && this.opciones.length) {
          if (this.indiceActivo > -1) { evento.preventDefault(); this.marcar(this.opciones.length - 1); }
          return;
        }

        if (tecla === 'Enter') {
          var activa = this.opciones[this.indiceActivo];
          if (this.abierto && activa) {
            var enlace = activa.querySelector('a');
            if (enlace && enlace.href) {
              evento.preventDefault();
              window.location.href = enlace.href;
            }
          }
          /* Sin opción activa el formulario se envía normalmente a /search. */
        }
      }

      marcar(indice) {
        this.desmarcar();
        var opcion = this.opciones[indice];
        if (!opcion) { return; }
        this.indiceActivo = indice;
        opcion.setAttribute('aria-selected', 'true');
        opcion.classList.add('sc-ps__opcion--activa');
        if (opcion.id) { this.entrada.setAttribute('aria-activedescendant', opcion.id); }
        if (typeof opcion.scrollIntoView === 'function') {
          opcion.scrollIntoView({ block: 'nearest' });
        }
      }

      desmarcar() {
        this.opciones.forEach(function (opcion) {
          opcion.setAttribute('aria-selected', 'false');
          opcion.classList.remove('sc-ps__opcion--activa');
        });
        this.indiceActivo = -1;
        this.entrada.removeAttribute('aria-activedescendant');
      }

      /* Envío y cierre por foco o clic */

      alEnviar() {
        window.clearTimeout(this.temporizador);
        this.abortar();
        this.cerrar();
      }

      alClicFuera(evento) {
        if (!this.abierto) { return; }
        if (this.contains(evento.target)) { return; }
        this.cerrar();
      }

      alSalirFoco() {
        var self = this;
        window.clearTimeout(this.temporizadorCierre);
        this.temporizadorCierre = window.setTimeout(function () {
          if (!self.contains(document.activeElement)) { self.cerrar(); }
        }, RETARDO_CIERRE_MS);
      }
    }

    return PredictiveSearch;
  }

  if ('customElements' in window && !customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearchDefinicion());
  }
})();
